import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/35 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-lg"></i>
            </div>
            <span className="font-heading text-base md:text-lg font-semibold whitespace-nowrap text-background-50">
              AllChinaBuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-0.5">
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  isScrolled
                    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 rounded-xl border border-background-200/80 overflow-hidden animate-fade-in">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/articles/shopping-guide-2026"
              className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                  : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              AllChinaBuy Sheet
            </a>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors text-background-50 hover:bg-white/10"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-fade-in max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className="ri-home-line text-sm"></i>
              </div>
              Home
            </Link>

            <Link
              to="/articles/shopping-guide-2026"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                <i className="ri-book-open-line text-sm"></i>
              </div>
              Shopping Guide
            </Link>

            <div className="border-t border-background-200/70 pt-3 mt-1">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-2 pb-2">14 Categories</p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-foreground-700 hover:bg-background-100 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="w-7 h-7 flex-shrink-0 rounded-md overflow-hidden">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-3 mt-1 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500">
                  <i className="ri-external-link-line text-sm"></i>
                </div>
                AllChinaBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors"
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}